import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  // Customer basic information
  id: '',
  first_name: '',
  last_name: '',
  primary_address_street: null,
  primary_address_street2: null,
  primary_address_state: null,
  primary_address_city: null,
  primary_address_postalcode: null,
  phone_mobile: null,
  email1: '',
  auto_generated_id_c: '',
  folio_number_c: '',
  processing_location: null,
  store: '',
  all_factura_processed: '0',
  phone_verified_code: false,
  email_is_verified: '0',

  // Additional customer fields from API
  date_entered: '',
  date_modified: '',
  modified_user_id: null,
  created_by: '',
  description: null,
  deleted: '0',
  assigned_user_id: '',
  salutation: null,
  title: null,
  photo: null,
  department: null,
  do_not_call: '0',
  phone_home: null,
  phone_work: null,
  phone_other: null,
  phone_fax: null,
  lawful_basis: null,
  date_reviewed: null,
  lawful_basis_source: null,
  primary_address_country: null,
  alt_address_street: null,
  alt_address_city: null,
  alt_address_state: null,
  alt_address_postalcode: null,
  alt_address_country: null,
  assistant: null,
  assistant_phone: null,
  lead_source: 'Portal Entry',
  reports_to_id: '',
  birthdate: null,
  campaign_id: '',
  joomla_account_id: null,
  portal_account_disabled: '0',
  portal_user_type: 'Single',
  phone_verified_c: '0',
  country_code_c: null,
  agent_id_c: '',
  status_c: 'Refund Request Created',
  previous_status_c: '',
  zendesk_id: null,
  is_zendesk_created: '0',
  affiliate_company_id: '',
  batch_id: null,
  agreed_to_contaced: '0',
  is_accepted_terms_and_conditions: '0',
  status: null,
  sharefile_folder_id: '',
  trip_type: 'air',
  portal_status_c: null,
  intercom_id: null,
  is_intercom_created: '0',
  affiliate_location_id: '',
  affiliate_region_id: '',
  affiliate_store_id: '',
  id_c: '',
  jjwg_maps_lng_c: '0.00000000',
  jjwg_maps_lat_c: '0.00000000',
  jjwg_maps_geocode_status_c: '',
  jjwg_maps_address_c: '',
  agent_name: '',
  docs_uploaded_c: '',
  processing_statuses_c: '',
  status_override: '0',
  apply_10_percent_iva_refund: '0',
  // Image preview functionality
  imagePreview: {
    src: '',
    type: 'image',
    fileName: '',
    preview: false,
    documentType: null,
    cardId: null,
    imageIndex: 0,
    refreshKey: 0,
    isNewRelation: false,
    isVaultSource: false,
    vaultImageUrl: null,
    vaultImages: null
    // isOCRCompleted: false
  },
  // Unmasked card data per cardId
  unmaskedCards: {},
  // Whether the credit card image mask (redaction) edit mode is active
  maskEditModeActive: false,
  // Active tab index for the Passport/Cards image viewer (0 = Passport)
  activePassportCardTab: 0,
  // Loading states
  loading: false,
  error: null,

  // Cache-busting counter for thumbnail images after OCR/redaction
  imageRefreshCounter: 0,

  // New customer flag
  newCustomer: false
}

export const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    // Customer data management
    setCustomer: (state, action) => {
      return { ...state, ...action.payload }
    },
    setCustomerFromAPI: (state, action) => {
      return { ...state, ...action.payload }
    },
    updateCustomerField: (state, action) => {
      const { field, value } = action.payload
      state[field] = value
    },
    initializeCustomer: () => initialState,

    // Image preview functionality
    showImagePreview: (state, action) => {
      const {
        name = '',
        docId = '',
        preview = false,
        documentType = null,
        cardId = null,
        refreshKey,
        imageIndex = 0,
        isNewRelation = false,
        isVaultSource,
        vaultImageUrl,
        vaultImages
      } = action.payload

      const preserveVaultSession =
        preview &&
        cardId &&
        cardId === state.imagePreview?.cardId &&
        state.imagePreview?.isVaultSource &&
        isVaultSource !== true &&
        vaultImageUrl === undefined &&
        vaultImages === undefined

      const hasVaultInPayload =
        isVaultSource === true ||
        !!vaultImageUrl ||
        (Array.isArray(vaultImages) && vaultImages.length > 0)

      const resolvedIsVaultSource =
        preview && (hasVaultInPayload || preserveVaultSession)

      const resolvedVaultImageUrl = preview
        ? vaultImageUrl !== undefined
          ? vaultImageUrl
          : preserveVaultSession
            ? state.imagePreview.vaultImageUrl
            : null
        : null

      const resolvedVaultImages = preview
        ? vaultImages !== undefined
          ? vaultImages
          : preserveVaultSession
            ? state.imagePreview.vaultImages
            : null
        : null

      state.imagePreview = {
        src: docId,
        type: 'image',
        fileName: name,
        preview,
        documentType,
        cardId,
        refreshKey:
          refreshKey !== undefined
            ? refreshKey
            : state.imagePreview?.refreshKey || 0,
        imageIndex,
        isNewRelation,
        isVaultSource: resolvedIsVaultSource,
        vaultImageUrl: resolvedVaultImageUrl,
        vaultImages: resolvedVaultImages
      }
    },
    refreshImagePreview: state => {
      state.imagePreview.refreshKey = (state.imagePreview?.refreshKey || 0) + 1
    },
    hideImagePreview: state => {
      state.imagePreview = initialState.imagePreview
    },
    // Unmasked card data management
    setUnmaskedCard: (state, action) => {
      const { cardId, cardNumber, imageUrl, images } = action.payload
      if (cardId) {
        const existingCard = state.unmaskedCards[cardId]
        state.unmaskedCards[cardId] = {
          cardNumber,
          imageUrl: imageUrl || null,
          images: images || (imageUrl ? [{ url: imageUrl, index: 1 }] : null),
          refreshKey: existingCard?.refreshKey || 0
        }
      }
    },
    refreshCardImage: (state, action) => {
      const { cardId } = action.payload
      if (cardId && state.unmaskedCards[cardId]) {
        state.unmaskedCards[cardId].refreshKey =
          (state.unmaskedCards[cardId].refreshKey || 0) + 1
      }
    },
    clearUnmaskedCard: (state, action) => {
      const { cardId } = action.payload
      if (cardId && state.unmaskedCards[cardId]) {
        delete state.unmaskedCards[cardId]
      }
    },
    clearAllUnmaskedCards: state => {
      state.unmaskedCards = {}
    },

    // Credit card image mask (redaction) edit mode management
    setMaskEditMode: (state, action) => {
      state.maskEditModeActive = !!action.payload
    },

    // Active Passport/Cards image viewer tab management
    setActivePassportCardTab: (state, action) => {
      state.activePassportCardTab = action.payload
    },

    incrementImageRefreshCounter: state => {
      state.imageRefreshCounter = (state.imageRefreshCounter || 0) + 1
    },

    // Loading and error states
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    clearError: state => {
      state.error = null
    },

    // New customer flag management
    setNewCustomer: (state, action) => {
      state.newCustomer = action.payload
    },

    resetCustomer: () => initialState
  }
})

export const {
  setCustomer,
  setCustomerFromAPI,
  updateCustomerField,
  initializeCustomer,
  showImagePreview,
  refreshImagePreview,
  hideImagePreview,
  setUnmaskedCard,
  refreshCardImage,
  clearUnmaskedCard,
  clearAllUnmaskedCards,
  setMaskEditMode,
  setActivePassportCardTab,
  incrementImageRefreshCounter,
  setLoading,
  setError,
  clearError,
  setNewCustomer,
  resetCustomer,
  setCurrentUserInformation
} = customerSlice.actions

// Selectors
export const selectCustomer = state => state.customer
export const selectCustomerBasicInfo = state => ({
  id: state.customer.id,
  first_name: state.customer.first_name,
  last_name: state.customer.last_name,
  email1: state.customer.email1,
  phone_mobile: state.customer.phone_mobile,
  phone_verified_c: state.customer.phone_verified_c
})
export const selectCustomerAddress = state => ({
  primary_address_street: state.customer.primary_address_street,
  primary_address_street2: state.customer.primary_address_street2,
  primary_address_state: state.customer.primary_address_state,
  primary_address_city: state.customer.primary_address_city,
  primary_address_postalcode: state.customer.primary_address_postalcode
})
export const selectCustomerStatus = state => state.customer.status_c
export const selectCustomerPreviousStatus = state => state.customer.previous_status_c
export const selectCustomerImagePreview = state => state.customer.imagePreview
export const selectCustomerLoading = state => state.customer.loading
export const selectCustomerError = state => state.customer.error
export const selectNewCustomer = state => state.customer.newCustomer
export const selectImageRefreshCounter = state => state.customer.imageRefreshCounter || 0
export const selectMaskEditModeActive = state => state.customer.maskEditModeActive || false
export default customerSlice.reducer
